import { memo, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { first, isEmpty, isNil } from 'lodash';
// Import UI Components
import { BusyLoader, Icon, SkeletonLoader, Tooltip } from '@geneui/components';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Constants
import {
    BonusTriggerType,
    BonusTypesLabels,
    KPISources,
    l,
    ObjectTypeLabelMap,
    ObjectTypesMap,
} from 'constants/common';

const BonusCalculationLogic = ({ campaignBonus, bonusInfo, isLoading, isVertical, isGrid }) => {
    const { t } = useTranslation();
    const { doGetRequest } = useRequest();

    const [bonusCalculateAction, setBonusCalculateAction] = useState<string>();
    const [bonusKPI, setBonusKPI] = useState<string>();
    const [isLoadingMarketplaces, setIsLoadingMarketplaces] = useState(false);
    const [marketplace, setMarketplace] = useState(true);

    const { getBonusCalculateActionsRequest, getBonusObjectsByTypeRequest, getNftMarketplacesRequest } =
        useCancelableRequests({
            getBonusCalculateActionsRequest: UtilsHttpService.getBonusCalculateActions,
            getBonusObjectsByTypeRequest: UtilsHttpService.getBonusObjectsByType,
            getNftMarketplacesRequest: UtilsHttpService.getNftMarketplaces,
        });

    const isBonusTriggerTypeManual = campaignBonus?.TriggerType === BonusTriggerType.Manual;
    const isNFTBonus = bonusInfo?.Type === BonusTypesLabels.NFTBonus;
    const isObjectTypeKPI = campaignBonus?.BonusConfig?.ObjectType === ObjectTypesMap.KPI;
    const isObjectTypeFromFile = campaignBonus?.BonusConfig?.ObjectType === ObjectTypesMap.FromFile;
    const bonusDetails = bonusInfo?.BonusDetails as AnyObject[];

    const nftBonusDetails = useMemo(() => first(bonusDetails), [bonusDetails]);

    const className = classNames('', {
        'key-value-h': !isVertical,
        'key-value-v': isVertical,
        'bc-grid-layout-item-4': isGrid,
    });

    useEffect(() => {
        if (isNFTBonus && !isEmpty(bonusDetails)) {
            setIsLoadingMarketplaces(true);
            doGetRequest(getNftMarketplacesRequest.request, {
                successCallback: (data) => {
                    const marketplaceValue = first(bonusDetails)?.Marketplace;
                    setMarketplace((data ?? []).find((item) => item.Value === marketplaceValue)?.DisplayName);
                },
            }).then(() => setIsLoadingMarketplaces(false));
        }
    }, [bonusDetails, doGetRequest, getNftMarketplacesRequest, isNFTBonus]);

    const init = () => {
        if (!isNil(campaignBonus?.BonusConfig)) {
            if (isObjectTypeFromFile) {
                setBonusKPI(l.FromFile);
            } else {
                if (isObjectTypeKPI) {
                    doGetRequest(getBonusObjectsByTypeRequest.request, {
                        queryString: { type: KPISources.KPI },
                        successCallback: (Data) => {
                            const kpi = Data.find(
                                (element) => +element.Id === campaignBonus?.BonusConfig?.ColumnInfoId,
                            );
                            setBonusKPI(kpi?.Name);
                        },
                    });
                } else {
                    doGetRequest(getBonusCalculateActionsRequest.request, {
                        successCallback: (Data) => {
                            const action = Data.find((element) => +element.Id === campaignBonus?.BonusConfig?.Function);
                            setBonusCalculateAction(action?.Name);
                        },
                    });
                    doGetRequest(getBonusObjectsByTypeRequest.request, {
                        queryString: { type: KPISources.Object },
                        successCallback: (Data) => {
                            const object = Data.find(
                                (element) => +element.Id === campaignBonus?.BonusConfig?.ObjectType,
                            );
                            setBonusKPI(object?.Name);
                        },
                    });
                }
            }
        }
    };
    const bonusInfoLabel = useMemo(() => {
        if (!isNil(bonusInfo)) {
            return [
                BonusTypesLabels.NftVoucherBonus,
                BonusTypesLabels.WonderWheelSpin,
                BonusTypesLabels.FreeSpin,
                BonusTypesLabels.LoyaltyPoint,
            ].includes(bonusInfo?.Type)
                ? l.Count
                : l.Amount;
        }
    }, [bonusInfo]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    return (
        <BusyLoader isBusy={isLoading} type="spinner" spinnerSize="medium">
            <div className={classNames({ 'bc-grid-layout-container': isGrid })}>
                {!isNil(campaignBonus) && (
                    <>
                        <div className={className}>
                            <span className="ellipsis-text kv-label">
                                {t(l.Wallet)}
                                <Tooltip text={t(l.BonusWalletInfo)}>
                                    <Icon type="bc-icon-info" />
                                </Tooltip>
                            </span>
                            <span className="ellipsis-text kv-value kv-value-css">
                                {isNil(campaignBonus.Wallet) ? t(l.Standart) : campaignBonus.Wallet}
                            </span>
                        </div>
                        {isBonusTriggerTypeManual && (
                            <div className={className}>
                                <span className="ellipsis-text kv-label">{t(l.Method)}</span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {isNil(campaignBonus.BonusConfig) ? t(l.Static) : t(l.Dynamic)}
                                </span>
                            </div>
                        )}
                        {isBonusTriggerTypeManual && !isNil(campaignBonus.BonusConfig) && (
                            <div className={className}>
                                <span className="ellipsis-text kv-label">{t(l.Option)}</span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {t(ObjectTypeLabelMap[campaignBonus.BonusConfig.ObjectType] ?? KPISources.Object)}
                                </span>
                            </div>
                        )}
                    </>
                )}
                {!isNil(campaignBonus) &&
                    !isNFTBonus &&
                    (isNil(campaignBonus.BonusConfig) ? (
                        <>
                            {!isEmpty(campaignBonus.Amounts) && (
                                <>
                                    {campaignBonus.Amounts.map(({ CurrencyCode, OriginalValue }, index) => {
                                        return (
                                            <div key={index} className={className}>
                                                <span className="ellipsis-text kv-label">
                                                    {t(bonusInfoLabel ?? '')}
                                                </span>
                                                <span className="ellipsis-text kv-value kv-value-css">
                                                    {OriginalValue} {CurrencyCode}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className={className}>
                                <span className="ellipsis-text kv-label">{t(l.ConsiderLimits)}</span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {campaignBonus.BonusConfig.FixOutOfRangeBonus ? t(l.On) : t(l.Off)}
                                </span>
                            </div>
                            {!isObjectTypeKPI && !isObjectTypeFromFile && (
                                <div className={className}>
                                    <span className="ellipsis-text kv-label">{t(l.BonusCalculateAction)}</span>
                                    <span className="ellipsis-text kv-value kv-value-css">
                                        {t(bonusCalculateAction ?? l.NA)}
                                    </span>
                                </div>
                            )}
                            {!isObjectTypeFromFile && (
                                <div className={className}>
                                    <span className="ellipsis-text kv-label">
                                        {t(isObjectTypeKPI ? l.BonusKPI : l.BonusObjectKind)}
                                    </span>
                                    <span className="ellipsis-text kv-value kv-value-css">{t(bonusKPI ?? '')}</span>
                                </div>
                            )}
                            <div className={className}>
                                <span className="ellipsis-text kv-label">{t(l.Percentage)}</span>
                                <span className="ellipsis-text kv-value kv-value-css">
                                    {campaignBonus.BonusConfig.Percentage}%
                                </span>
                            </div>
                        </>
                    ))}
                {isNFTBonus && (
                    <>
                        <div className={className}>
                            <span className="ellipsis-text kv-label">{t(l.Marketplace)}</span>
                            <SkeletonLoader
                                isBusy={isLoadingMarketplaces}
                                className="ellipsis-text kv-value kv-value-css"
                            >
                                {!isLoadingMarketplaces && (
                                    <span className="ellipsis-text kv-value kv-value-css">{marketplace}</span>
                                )}
                            </SkeletonLoader>
                        </div>
                        <div className={className}>
                            <span className="ellipsis-text kv-label">{t(l.From)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">{nftBonusDetails?.From}</span>
                        </div>
                        <div className={className}>
                            <span className="ellipsis-text kv-label">{t(l.To)}</span>
                            <span className="ellipsis-text kv-value kv-value-css">{nftBonusDetails?.To}</span>
                        </div>
                    </>
                )}
            </div>
        </BusyLoader>
    );
};

BonusCalculationLogic.propTypes = {
    campaignBonus: PropTypes.object,
    bonusInfo: PropTypes.object,
    isLoading: PropTypes.bool.isRequired,
    isVertical: PropTypes.bool,
    isGrid: PropTypes.bool,
};

export default memo(BonusCalculationLogic);
